import { useTranslation } from 'react-i18next';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { TabGroup, TabButton } from '../../ui/Tabs/index.js';
import { formatPeriodTick } from '../reliability/chart-utils.js';
import type {
  SreOpenBugTrendPoint,
  SreOpenBugTrendUnit,
} from '../../../types/sre-open-bug-trend.js';

/**
 * 減った数を 0 より下に伸ばすための dataKey。ツールチップでは絶対値に戻して見せる
 * @graph-connects none
 */
const CLOSED_BELOW_ZERO_KEY = 'closedBelowZero';

/** タブの並び順 @graph-connects none */
const UNITS: readonly SreOpenBugTrendUnit[] = ['week', 'month', 'half'];

/**
 * 期間キーを表示用に整える。半期は開始月 YYYY-MM を「YYYY-MM〜MM」（例: 2026-07〜12）にし、
 * 週次・月次は信頼性ページと同じ目盛り表記にする
 * @graph-connects formatPeriodTick [calls] 週次X軸目盛りの短縮フォーマット
 */
export function formatSrePeriod(period: string, unit: SreOpenBugTrendUnit): string {
  if (unit !== 'half') return formatPeriodTick(period, unit);
  const endMonth = Number(period.slice(5, 7)) + 5;
  return `${period}〜${String(endMonth).padStart(2, '0')}`;
}

export interface SreOpenBugTrendChartProps {
  /** 期間ごとの増えた数・減った数・期間末の未完了件数 */
  data: SreOpenBugTrendPoint[];
  /** 集計の開始日（YYYY-MM-DD）。説明文に出す */
  startDate: string | undefined;
  /** 読み込み状態 */
  isLoading: boolean;
  /** 集計単位（週次は月曜始まり、半期は 7〜12 月 / 1〜6 月） */
  unit: SreOpenBugTrendUnit;
  /** 集計単位切り替え時のコールバック */
  onUnitChange: (unit: SreOpenBugTrendUnit) => void;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business KPI画面のSREビューの「未完了バグ件数の増減」チャート。期間ごとに増えた数を上向き、減った数を下向きの棒で、期間末の未完了件数を折れ線で重ねて表示し、週次/月次/半期を切り替えられる
 * @graph-connects Card [calls] カードコンポーネント描画
 * @graph-connects TabGroup [calls] 週次/月次/半期トグル描画
 * @graph-connects ComposedChart [calls] 増減の棒と未完了件数の折れ線の描画
 * @graph-connects formatSrePeriod [calls] 期間キーの表示用整形
 */
export function SreOpenBugTrendChart({
  data,
  startDate,
  isLoading,
  unit,
  onUnitChange,
}: SreOpenBugTrendChartProps) {
  const { t } = useTranslation();

  const chartData = data.map((point) => ({
    ...point,
    [CLOSED_BELOW_ZERO_KEY]: -point.closed,
  }));

  const tooltipStyle = {
    backgroundColor: 'var(--color-bg-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
  };

  return (
    <Card className="animate-content-reveal">
      <CardHeader
        title={t('kpi.sreOpenBugTrend.title')}
        description={t('kpi.sreOpenBugTrend.description', { startDate: startDate ?? '' })}
        action={
          <TabGroup ariaLabel={t('kpi.sreOpenBugTrend.unitSwitch')}>
            {UNITS.map((option) => (
              <TabButton
                key={option}
                active={unit === option}
                label={t(`kpi.sreOpenBugTrend.unit.${option}`)}
                onClick={() => {
                  onUnitChange(option);
                }}
              />
            ))}
          </TabGroup>
        }
      />
      <CardContent>
        {isLoading ? (
          <div className="h-80 rounded-lg bg-[var(--color-bg-elevated)] animate-pulse" />
        ) : chartData.length > 0 ? (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                stackOffset="sign"
                margin={{ top: 8, right: 16, left: 8, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="period"
                  stroke="var(--color-text-muted)"
                  fontSize={11}
                  tickFormatter={(v: string) => formatSrePeriod(v, unit)}
                />
                <YAxis
                  yAxisId="change"
                  stroke="var(--color-text-muted)"
                  fontSize={11}
                  tickFormatter={(v: number) => String(Math.abs(v))}
                />
                <YAxis
                  yAxisId="open"
                  orientation="right"
                  stroke="var(--color-text-muted)"
                  fontSize={11}
                />
                <ReferenceLine yAxisId="change" y={0} stroke="var(--color-text-muted)" />
                <Tooltip
                  contentStyle={tooltipStyle}
                  labelStyle={{ color: 'var(--color-text-primary)' }}
                  labelFormatter={(label: unknown) =>
                    unit === 'half' ? formatSrePeriod(String(label), unit) : String(label)
                  }
                  formatter={(value) => (typeof value === 'number' ? Math.abs(value) : value)}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar
                  yAxisId="change"
                  dataKey="opened"
                  stackId="change"
                  name={t('kpi.sreOpenBugTrend.opened')}
                  fill="var(--color-error)"
                />
                <Bar
                  yAxisId="change"
                  dataKey={CLOSED_BELOW_ZERO_KEY}
                  stackId="change"
                  name={t('kpi.sreOpenBugTrend.closed')}
                  fill="var(--color-success)"
                />
                <Line
                  yAxisId="open"
                  type="monotone"
                  dataKey="openCount"
                  name={t('kpi.sreOpenBugTrend.openCount')}
                  stroke="var(--color-accent-primary)"
                  strokeWidth={2}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-80 flex items-center justify-center border border-dashed border-[var(--color-border)] rounded-lg">
            <p className="text-[var(--color-text-muted)]">{t('kpi.sreOpenBugTrend.empty')}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
